export interface PDFExportParams {
  area: string;
  branch: string;
  activeEntity: string;
  activeSection: string;
  filename: string;
  periodLabel: string;
  extractedKPIs?: { title: string; value: string; change?: string }[];
  charts?: { title: string; imgData: string }[];
  tables?: { title: string; headers: string[]; rows: string[][] }[];
}

// Clean up text from unsupported unicode emojis and symbols for safe Helvetica PDF printing
export function cleanText(text: string): string {
  if (!text) return '';
  return text
    .replace(/✓/g, '')
    .replace(/⏳/g, '')
    .replace(/✗/g, '')
    .replace(/▲/g, '+')
    .replace(/▼/g, '-')
    .replace(/[—–]/g, '-')             // Convert em-dash/en-dash to regular hyphens
    .replace(/[^\x20-\x7E\n\r\t]/g, '') // Keep standard printable ASCII characters
    .replace(/\s+/g, ' ')               // Normalize multiple spaces to single space
    .trim();
}

export function exportDashboardToPDF(params: PDFExportParams): void {
  import('jspdf').then(({ default: jsPDF }) => {
    // Dynamic layout orientation depending on table column counts and sections
    const isWideSection = params.activeSection === 'bep' || (params.tables && params.tables.some(t => t.headers.length >= 7));
    const orientation = isWideSection ? 'landscape' : 'portrait';

    const doc = new jsPDF({
      orientation: orientation,
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth(); // 297 if landscape, 210 if portrait
    const pageHeight = doc.internal.pageSize.getHeight(); // 210 if landscape, 297 if portrait
    let currentY = 15;

    // Background drawing for clean light mode corporate report
    const drawPageBackground = () => {
      doc.setFillColor(252, 252, 253); // #FCFCFD Alabaster
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Electric purple line on left edge for a sleek accent
      doc.setFillColor(139, 92, 246); // Purple 500
      doc.rect(0, 0, 1.5, pageHeight, 'F');
    };

    const checkPageBreak = (neededHeight: number) => {
      if (currentY + neededHeight > pageHeight - 15) {
        doc.addPage();
        drawPageBackground();
        currentY = 15;
      }
    };

    // First page initial background
    drawPageBackground();

    // --- Title Section ---
    doc.setTextColor(15, 23, 42); // #0F172A Slate 900
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    const entityName = params.activeEntity === 'JUARA_GADAI' ? 'Juara Gadai Group' : 'Sahabat Gadai / Juara Gadai';
    doc.text(`${entityName}`, 15, currentY + 6);
    currentY += 12;

    // --- Metadata Badge Card ---
    doc.setDrawColor(226, 232, 240); // Slate 200
    doc.setFillColor(248, 250, 252); // Slate 50
    doc.setLineWidth(0.4);
    doc.roundedRect(15, currentY, pageWidth - 30, 24, 2, 2, 'FD');

    // Metadata Text
    doc.setTextColor(71, 85, 105); // Slate 600
    doc.setFontSize(9.5);
    doc.setFont("helvetica", "normal");
    const loc = params.branch !== 'ALL' ? `Cabang: ${params.branch}` : `Area: ${params.area}`;
    const generated = new Date().toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    
    // Map section IDs to human readable labels
    const sectionNames: Record<string, string> = {
      'executive': 'Executive Summary',
      'dashboard': 'Financial Dashboard',
      'revenue': 'Detail Pendapatan (Revenue)',
      'expenses': 'Detail Biaya (Expenses)',
      'balance': 'Balance Sheet (Neraca Keuangan)',
      'cashflow': 'Cash Flow Statement',
      'monthly': 'Laporan Bulanan',
      'annual': 'Laporan Tahunan',
      'forecast': 'Analisis Proyeksi (Forecast)',
      'bep': 'Status Titik Impas (BEP)',
      'budget': 'Budget vs Actual Variance',
      'budget_plan': 'Rencana Anggaran (Budget Plan)',
      'gl_lookup': 'Buku Besar (General Ledger Lookup)',
      'database': 'Database Keuangan',
    };
    const activeSectionLabel = sectionNames[params.activeSection] || params.activeSection.toUpperCase();

    doc.text(`Periode Kinerja: ${params.periodLabel}`, 20, currentY + 7);
    doc.text(`Lokasi Data: ${loc}`, 20, currentY + 13);
    doc.text(`Modul Pelaporan: ${activeSectionLabel}`, 20, currentY + 19);
    
    doc.setFontSize(8.5);
    doc.setTextColor(148, 163, 184); // Slate 400
    doc.text(`Dicetak pada: ${generated}`, pageWidth - 20, currentY + 19, { align: 'right' });

    currentY += 34;

    // --- Descriptive Context Narrative ---
    doc.setTextColor(51, 65, 85); // Slate 700
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    
    const contextMap: Record<string, string> = {
      'executive': `Laporan ini menyajikan Executive Summary (Ikhtisar Eksekutif) yang komprehensif untuk ${loc} selama periode ${params.periodLabel}. Metrik utama seperti Kesehatan Keuangan (Financial Health Score), perbandingan pendapatan terhadap pengeluaran operasional, kontribusi cabang terbaik, dan marjin laba dihitung secara real-time berdasarkan data historis untuk mendukung keputusan strategis manajemen puncak.`,
      'dashboard': `Laporan Dashboard Utama menyajikan ringkasan kinerja keuangan ${loc} untuk periode ${params.periodLabel}. Dokumen ini menyoroti metrik performa utama (Revenue, OpEx, Net Profit, Cash Flow) serta tren historis bulanan yang divisualisasikan dalam bentuk grafik analitik terpadu.`,
      'revenue': `Laporan Detail Pendapatan (Revenue) menjabarkan performa omset penjualan dan analisis penerimaan kas ${loc} sepanjang periode ${params.periodLabel}. Bagian ini membedah pertumbuhan pendapatan bulanan serta kontribusi masing-masing cabang untuk mengidentifikasi area pertumbuhan utama.`,
      'expenses': `Laporan Detail Pengeluaran (Expenses) merinci alokasi beban biaya operasional (OpEx) ${loc} untuk periode ${params.periodLabel}. Visualisasi menyajikan pembagian pos biaya utama (Gaji, Sewa, Operasional, Penyusutan) dan peringkat cabang dengan beban biaya tertinggi sebagai bahan evaluasi efisiensi biaya.`,
      'balance': `Laporan Neraca Posisi Keuangan (Balance Sheet) menyajikan struktur Aset, Liabilitas, dan Ekuitas ${loc} pada bulan acuan. Neraca ini penting untuk mengevaluasi rasio likuiditas (Current Ratio), struktur permodalan (Debt-to-Equity), dan modal kerja bersih (Working Capital) guna menilai kesehatan solvensi korporasi.`,
      'cashflow': `Laporan Arus Kas (Cash Flow) merinci aliran kas masuk dan keluar ${loc} sepanjang periode ${params.periodLabel}, terbagi ke dalam aktivitas Operasional, Investasi, dan Pendanaan. Dokumen ini menggambarkan kemampuan entitas dalam menghasilkan kas bersih untuk membiayai pertumbuhan bisnis.`,
      'monthly': `Laporan Laba Rugi Bulanan (Monthly P&L Report) menjabarkan performa operasional bulanan secara konsisten di bawah area ${params.area} selama periode ${params.periodLabel}, menyajikan tren performa bulan-ke-bulan secara detail.`,
      'annual': `Laporan Kinerja Tahunan menyajikan rekapitulasi laba rugi terakumulasi ${loc} untuk periode ${params.periodLabel}, membandingkan pencapaian tahun berjalan dengan tren makro perusahaan.`,
      'forecast': `Laporan Analisis Proyeksi (Forecast) ini memproyeksikan perkiraan posisi laba rugi ${loc} untuk 6 bulan ke depan. Proyeksi didasarkan pada rata-rata kinerja 3 bulan terakhir dan disesuaikan dengan asumsi persentase pertumbuhan pendapatan dan pengeluaran operasional yang disetel pengguna di layar.`,
      'bep': `Laporan Status Titik Impas (BEP) menguji status profitabilitas investasi cabang di bawah area ${params.area}. Melalui rekapitulasi laba bersih kumulatif, sistem menandai cabang mana saja yang telah melampaui batas modal awal (Pass) dan mana yang masih dalam proses pemulihan investasi (Ongoing).`,
      'budget': `Laporan Budget vs Actual Variance membandingkan target anggaran biaya dan pendapatan yang direncanakan dengan realisasi aktual di lapangan sepanjang ${params.periodLabel}. Laporan ini memaparkan variansi nilai nominal dan persentase untuk menentukan efektivitas penghematan atau deviasi pengeluaran.`,
      'budget_plan': `Laporan Rencana Anggaran (Budget Plan) merinci perencanaan alokasi dana dan proyeksi pengeluaran operasional untuk periode berjalan di bawah entitas ${entityName}.`,
      'gl_lookup': `Dokumen Buku Besar (General Ledger Lookup) menyajikan detail riwayat posting jurnal transaksi akuntansi ${loc} untuk keperluan audit dan penelusuran mutasi akun keuangan secara mendalam.`,
      'database': `Laporan database menyajikan data transaksi mentah yang tersimpan dalam sistem informasi keuangan entitas ${entityName}.`,
    };

    const defaultContext = `Laporan ini menyajikan ringkasan visual, indikator kinerja (KPI), serta tabel data komprehensif dari modul ${activeSectionLabel} yang sedang aktif pada sistem dashboard ${entityName} untuk wilayah ${loc} selama periode ${params.periodLabel}.`;
    const contextText = contextMap[params.activeSection] || defaultContext;

    const splitText = doc.splitTextToSize(contextText, pageWidth - 30);
    checkPageBreak(splitText.length * 5 + 10);
    doc.text(splitText, 15, currentY);
    currentY += (splitText.length * 5) + 8;

    // Line separator
    doc.setDrawColor(226, 232, 240);
    doc.line(15, currentY, pageWidth - 15, currentY);
    currentY += 10;

    // --- Draw Screen KPIs if present ---
    if (params.extractedKPIs && params.extractedKPIs.length > 0) {
      checkPageBreak(35);
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(15, 23, 42);
      doc.text("Ringkasan Indikator Utama (KPIs)", 15, currentY);
      currentY += 7;

      const maxCardsPerRow = pageWidth > 220 ? 4 : 3;
      const printableWidth = pageWidth - 30;
      const gap = 6;
      const cardW = (printableWidth - (gap * (maxCardsPerRow - 1))) / maxCardsPerRow;
      const cardH = 18;
      let xPos = 15;

      doc.setFontSize(8.5);
      doc.setFont("helvetica", "normal");

      params.extractedKPIs.forEach((kpi, idx) => {
        // Arrange KPIs in rows
        if (idx > 0 && idx % maxCardsPerRow === 0) {
          xPos = 15;
          currentY += cardH + gap;
          checkPageBreak(cardH + 10);
        }

        // Draw light grey metric card box
        doc.setFillColor(248, 250, 252);
        doc.setDrawColor(241, 245, 249);
        doc.roundedRect(xPos, currentY, cardW, cardH, 1.5, 1.5, 'FD');

        // Draw KPI Title
        doc.setFont("helvetica", "bold");
        doc.setTextColor(100, 116, 139); // Slate 500
        doc.setFontSize(7.5);
        doc.text(cleanText(kpi.title).toUpperCase(), xPos + 3.5, currentY + 5);

        // Draw KPI Value
        doc.setFont("helvetica", "bold");
        doc.setTextColor(15, 23, 42); // Slate 900
        doc.setFontSize(11);
        doc.text(cleanText(kpi.value), xPos + 3.5, currentY + 11.5);

        // Draw Change indicator if present
        if (kpi.change) {
          const cleanedChange = cleanText(kpi.change);
          const isPos = cleanedChange.includes('+') || !cleanedChange.includes('-');
          doc.setFontSize(7.5);
          if (isPos) {
            doc.setTextColor(16, 185, 129);
          } else {
            doc.setTextColor(239, 68, 68);
          }
          doc.text(cleanedChange, xPos + 3.5, currentY + 15.5);
        }

        xPos += cardW + gap;
      });

      currentY += cardH + 12;
    }

    // --- Draw Screen Charts (Canvases) if present ---
    if (params.charts && params.charts.length > 0) {
      params.charts.forEach((chart) => {
        // Center dynamically scaled high-res charts
        const imgWidth = pageWidth - 30;
        const imgHeight = pageWidth > 220 ? 95 : 90;
        
        checkPageBreak(imgHeight + 16);
        
        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(15, 23, 42);
        doc.text(cleanText(chart.title), 15, currentY);
        currentY += 5;
        
        doc.addImage(chart.imgData, 'PNG', 15, currentY, imgWidth, imgHeight);
        currentY += imgHeight + 12;
      });
    }

    // --- Draw Screen Tables dynamically if present ---
    const drawTable = (title: string, headers: string[], rows: string[][]) => {
      if (headers.length === 0 || rows.length === 0) return;
      
      checkPageBreak(25); // Needs space for title + header + at least one row
      
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(15, 23, 42); // Slate 900
      doc.text(cleanText(title), 15, currentY);
      currentY += 6;
      
      const colCount = headers.length;
      const tableWidth = pageWidth - 30; // Scale dynamically based on page orientation (Portrait vs Landscape)
      
      // Clean headers for matching
      const cleanHeaders = headers.map(h => 
        h.toLowerCase()
         .replace(/[^\x00-\x7F]/g, "") // strip emojis/non-ascii
         .trim()
      );
      
      let colWidths: number[] = [];
      
      // Smart Column Width Calculation
      const isBEPTable = cleanHeaders.includes("kode") && cleanHeaders.includes("nama cabang") && cleanHeaders.includes("gap to pass");
      const isPLDetailTable = cleanHeaders.includes("no acc") && cleanHeaders.includes("name account");
      
      if (isBEPTable) {
        // BEP table has 10 columns:
        // ["Kode", "Nama Cabang", "Kota", "Waktu Operasional", "Pendapatan", "Pengeluaran", "Keuntungan/Kerugian", "BEP Kumulatif", "Gap to Pass", "Status"]
        const weights = [
          0.04, // Kode
          0.15, // Nama Cabang
          0.06, // Kota
          0.10, // Waktu Operasional
          0.11, // Pendapatan
          0.11, // Pengeluaran
          0.12, // Keuntungan/Kerugian
          0.13, // BEP Kumulatif
          0.13, // Gap to Pass
          0.05, // Status
        ];
        if (colCount === weights.length) {
          colWidths = weights.map(w => w * tableWidth);
        }
      } else if (isPLDetailTable) {
        // P&L Detail table has 5 columns:
        // ["No acc", "Name Account", "Current", "Prior", "Change"]
        const weights = [
          0.10, // No acc
          0.30, // Name Account
          0.20, // Current
          0.20, // Prior
          0.20, // Change
        ];
        if (colCount === weights.length) {
          colWidths = weights.map(w => w * tableWidth);
        }
      }
      
      // Fallback distributions if widths not set
      if (colWidths.length === 0) {
        if (colCount === 2) {
          colWidths = [tableWidth * 0.65, tableWidth * 0.35];
        } else if (colCount === 3) {
          colWidths = [tableWidth * 0.45, tableWidth * 0.275, tableWidth * 0.275];
        } else {
          const w = tableWidth / colCount;
          colWidths = Array(colCount).fill(w);
        }
      }
      
      const tableFontSize = isWideSection ? 8.2 : 9;

      // Draw Header Row
      doc.setFillColor(241, 245, 249); // Slate 100
      doc.rect(15, currentY, tableWidth, 7, 'F');
      
      doc.setFontSize(tableFontSize);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(51, 65, 85); // Slate 700
      
      let xOffset = 15;
      for (let c = 0; c < colCount; c++) {
        let header = cleanText(headers[c]);
        // Map long headers to shorter versions for better layout fit in wide tables
        if (isWideSection) {
          if (header.toLowerCase().includes('keuntungan/kerugian')) header = 'Laba/Rugi';
          if (header.toLowerCase().includes('waktu operasional')) header = 'Durasi';
        }
        const lowerHeader = header.toLowerCase();
        
        // Right align numeric headers
        const isNumeric = c > 0 && (
          lowerHeader.includes('amount') || 
          lowerHeader.includes('value') || 
          lowerHeader.includes('bep') || 
          lowerHeader.includes('gap') || 
          lowerHeader.includes('profit') || 
          lowerHeader.includes('revenue') || 
          lowerHeader.includes('opex') || 
          lowerHeader.includes('pendapatan') || 
          lowerHeader.includes('pengeluaran') || 
          lowerHeader.includes('keuntungan') || 
          lowerHeader.includes('kerugian') || 
          lowerHeader.includes('current') || 
          lowerHeader.includes('prior') || 
          lowerHeader.includes('change')
        );
        
        const textX = isNumeric ? xOffset + colWidths[c] - 3 : xOffset + 3;
        const alignOpt = isNumeric ? { align: 'right' as const } : undefined;
        
        doc.text(header, textX, currentY + 5, alignOpt);
        xOffset += colWidths[c];
      }
      currentY += 7;
      
      // Draw Rows
      doc.setFont("helvetica", "normal");
      rows.forEach((row, rIdx) => {
        checkPageBreak(7.5);
        
        // Alternating background color
        if (rIdx % 2 === 1) {
          doc.setFillColor(248, 250, 252); // Slate 50
          doc.rect(15, currentY, tableWidth, 7, 'F');
        }
        
        // Row bottom light border
        doc.setDrawColor(241, 245, 249); // Slate 100
        doc.setLineWidth(0.2);
        doc.line(15, currentY + 7, 15 + tableWidth, currentY + 7);
        
        xOffset = 15;
        for (let c = 0; c < colCount; c++) {
          const val = row[c] || '';
          const cleanedVal = cleanText(val);
          
          // Check if it's numeric/currency for right alignment
          const isNumeric = c > 0 && (
            cleanedVal.startsWith('Rp') || 
            cleanedVal.startsWith('-Rp') || 
            cleanedVal.startsWith('+') || 
            cleanedVal.startsWith('-') || 
            cleanedVal.endsWith('%') || 
            !isNaN(parseFloat(cleanedVal.replace(/[^0-9.-]/g, '')))
          );
          
          doc.setTextColor(30, 41, 59); // Slate 800
          if (cleanedVal.includes('Ongoing') || cleanedVal.includes('Loss') || (cleanedVal.startsWith('-') && !cleanedVal.includes('—'))) {
            doc.setTextColor(239, 68, 68); // Red for negative metrics / ongoing
          } else if (cleanedVal.includes('Pass') || cleanedVal.includes('Profit') || (cleanedVal.startsWith('+') && !cleanedVal.includes('—'))) {
            doc.setTextColor(16, 185, 129); // Green for positive/favorable / pass
          }
          
          const textX = isNumeric ? xOffset + colWidths[c] - 3 : xOffset + 3;
          const alignOpt = isNumeric ? { align: 'right' as const } : undefined;
          
          // Truncate text if too long to fit in cell (generous budget of 0.65)
          const maxChar = Math.floor(colWidths[c] * 0.65);
          const displayVal = cleanedVal.length > maxChar && !isNumeric ? cleanedVal.substring(0, maxChar - 3) + '...' : cleanedVal;
          
          doc.text(displayVal, textX, currentY + 5, alignOpt);
          xOffset += colWidths[c];
        }
        currentY += 7;
      });
      
      currentY += 10; // margin below table
    };

    if (params.tables && params.tables.length > 0) {
      params.tables.forEach((table) => {
        drawTable(table.title, table.headers, table.rows);
      });
    }

    doc.save(params.filename);
  });
}
